"use client";

import { useState } from "react";
import { simulateScoreIncrease } from "@/lib/scoring/score-simulator";
import type { ScoreResult } from "@/lib/scoring/types";
import { formatRatio } from "@/lib/format";
import { cn } from "@/lib/utils";

const DELTA_OPTIONS = [5, 10, 15, 20] as const;

/**
 * Score-Zusammensetzungs-Simulator ("was müsste sich ändern, damit der Score um X Punkte
 * steigt", backlog.md "Weiterer Nutzwert-Katalog") - reine, didaktische Rechnerei auf der
 * bereits vorhandenen 0-10-Punkte-Abstraktion je Bereich (siehe
 * lib/scoring/score-simulator.ts), keine KI-Schätzung, keine Handlungsempfehlung. Zeigt für
 * jeden Bereich EINZELN (alle anderen unverändert gedacht), wie viele Punkte er zusätzlich
 * bräuchte - Balken im selben Navy/Gold-Muster wie ScoreKpiGrid, kein Radar-Chart.
 */
export function ScoreSimulatorPanel({ score }: { score: ScoreResult }) {
  const [delta, setDelta] = useState<(typeof DELTA_OPTIONS)[number]>(5);
  const results = simulateScoreIncrease(score.kpis, score.total, delta);
  if (results.length === 0) return null;

  const anyReachable = results.some((r) => r.reachable);

  return (
    <div className="rounded-lg bg-muted p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-ink">Score-Simulator: was müsste sich ändern?</span>
        <div className="flex shrink-0 gap-1 rounded-full bg-card p-1">
          {DELTA_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDelta(d)}
              className={cn(
                "rounded-full px-2.5 py-1 font-tabular text-xs font-medium transition-colors",
                delta === d ? "bg-ink text-primary-foreground" : "text-muted-foreground hover:text-ink",
              )}
            >
              +{d}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-ink-secondary">
        Rein rechnerisch: wie viele Punkte EIN Bereich (alle anderen unverändert) bräuchte,
        damit der Gesamtscore um {delta} Punkte steigt - keine Prognose, kein Vorschlag, wie
        das erreicht werden könnte.
      </p>
      <div className="mt-3 flex flex-col gap-2.5">
        {results.map((r) => (
          <div key={r.key} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-secondary">{r.label}</span>
              <span className="font-tabular text-muted-foreground">
                {r.reachable
                  ? `${formatRatio(r.currentBasePoints, 1)} → ${formatRatio(r.neededBasePoints, 1)} / 10`
                  : "auch bei 10/10 nicht ausreichend"}
              </span>
            </div>
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-card">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-ink/35"
                style={{ width: `${(r.currentBasePoints / 10) * 100}%` }}
              />
              {r.pointsNeeded > 0 && (
                <div
                  className="absolute inset-y-0 rounded-full bg-gold"
                  style={{
                    left: `${(r.currentBasePoints / 10) * 100}%`,
                    width: `${(Math.min(r.neededBasePoints, 10) - r.currentBasePoints) * 10}%`,
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {!anyReachable && (
        <p className="mt-2.5 text-xs text-gold-text">
          Kein einzelner Bereich allein reicht für +{delta} Punkte aus - das bräuchte mehrere
          Bereiche gleichzeitig.
        </p>
      )}
    </div>
  );
}
